import React, { useEffect, useState, useMemo } from 'react';
import { Leaf, Plus, Search, Trash2, X, Save, RefreshCw } from 'lucide-react';
import { API_BASE, authFetch } from '../../constants';

/**
 * CropProfilesModal
 *
 * Two-pane settings panel (mirrors the kas_app SettingsDialog "Crop Profiles" tab).
 *
 * LEFT  – searchable list of all crop profiles.
 * RIGHT – editor for creating / updating a profile.
 *
 * A "crop profile" is a user-defined crop name (e.g. "Wine Grapes", "Table Grapes")
 * that maps to one of the official PAL lab-crop codes (e.g. Grapes → 180).
 * When a batch CSV is generated, the portal checks this table first before falling
 * back to the static crop_codes.py mapping.
 */
function CropProfilesModal({ onClose }) {
  // ── data ──────────────────────────────────────────────────────────────────
  const [labCrops, setLabCrops]       = useState([]);   // PAL master list
  const [profiles, setProfiles]       = useState([]);   // custom crop profiles
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);

  // ── list-pane state ───────────────────────────────────────────────────────
  const [search, setSearch]           = useState('');
  const [selectedId, setSelectedId]   = useState(null); // currently selected profile id

  // ── editor-pane state ─────────────────────────────────────────────────────
  const [editId, setEditId]           = useState(null); // null = new
  const [editName, setEditName]       = useState('');
  const [editLabCropId, setEditLabCropId] = useState('');
  const [editNotes, setEditNotes]     = useState('');
  const [editActive, setEditActive]   = useState(true);
  const [error, setError]             = useState('');
  const [successMsg, setSuccessMsg]   = useState('');

  // ── load data on mount ────────────────────────────────────────────────────
  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [lcRes, cpRes] = await Promise.all([
        authFetch(`${API_BASE}/lab-crops`),
        authFetch(`${API_BASE}/crop-profiles`),
      ]);
      setLabCrops(await lcRes.json());
      setProfiles(await cpRes.json());
    } catch (err) {
      console.error('Failed to load crop data:', err);
    } finally {
      setLoading(false);
    }
  };

  // ── filtered list ─────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const term = search.toLowerCase().trim();
    if (!term) return profiles;
    return profiles.filter(
      (p) =>
        p.name.toLowerCase().includes(term) ||
        p.lab_crop_name?.toLowerCase().includes(term) ||
        p.lab_crop_code?.includes(term)
    );
  }, [profiles, search]);

  // ── select a profile to edit ──────────────────────────────────────────────
  const handleSelect = (profile) => {
    setSelectedId(profile.id);
    setEditId(profile.id);
    setEditName(profile.name);
    setEditLabCropId(String(profile.lab_crop_id));
    setEditNotes(profile.notes || '');
    setEditActive(profile.active);
    setError('');
    setSuccessMsg('');
  };

  // ── start a new / blank profile ───────────────────────────────────────────
  const handleNew = () => {
    setSelectedId(null);
    setEditId(null);
    setEditName('');
    setEditLabCropId(labCrops.length ? String(labCrops[0].id) : '');
    setEditNotes('');
    setEditActive(true);
    setError('');
    setSuccessMsg('');
  };

  // ── save (create or update) ───────────────────────────────────────────────
  const handleSave = async () => {
    setError('');
    setSuccessMsg('');

    if (!editName.trim()) {
      setError('Profile name is required.');
      return;
    }
    if (!editLabCropId) {
      setError('Please select a PAL lab crop.');
      return;
    }

    setSaving(true);
    try {
      const body = JSON.stringify({
        name:        editName.trim(),
        lab_crop_id: parseInt(editLabCropId, 10),
        notes:       editNotes.trim() || null,
        active:      editActive,
      });

      let res;
      if (editId === null) {
        // Create
        res = await authFetch(`${API_BASE}/crop-profiles`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
        });
      } else {
        // Update
        res = await authFetch(`${API_BASE}/crop-profiles/${editId}`, {
          method:  'PUT',
          headers: { 'Content-Type': 'application/json' },
          body,
        });
      }

      if (!res.ok) {
        const data = await res.json();
        setError(data.detail || 'Save failed.');
      } else {
        const saved = await res.json();
        setSuccessMsg(`"${saved.name}" saved successfully.`);
        setEditId(saved.id);
        setSelectedId(saved.id);
        await loadAll();
      }
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // ── delete ────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!editId) return;
    if (!window.confirm(`Delete crop profile "${editName}"? This cannot be undone.`)) return;

    try {
      const res = await authFetch(`${API_BASE}/crop-profiles/${editId}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const data = await res.json();
        setError(data.detail || 'Delete failed.');
        return;
      }
      await loadAll();
      handleNew();
    } catch (err) {
      setError(`Error: ${err.message}`);
    }
  };

  // ── active lab crops (for dropdown) ──────────────────────────────────────
  const activeLabCrops = useMemo(
    () => labCrops.filter((lc) => lc.active).sort((a, b) => a.pal_name.localeCompare(b.pal_name)),
    [labCrops]
  );

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-5xl h-[88vh] flex flex-col shadow-2xl">

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50 rounded-t-lg">
          <div className="flex items-center gap-3">
            <Leaf size={22} className="text-green-600" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Crop Profiles</h2>
              <p className="text-xs text-gray-500">
                Map custom crop names (e.g. "Wine Grapes") to a PAL lab crop code (e.g. Grapes → 180)
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={22} />
          </button>
        </div>

        {/* ── Body ────────────────────────────────────────────────────────── */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            Loading…
          </div>
        ) : (
          <div className="flex flex-1 overflow-hidden">

            {/* ── LEFT: profile list ────────────────────────────────────── */}
            <div className="w-72 flex flex-col border-r bg-gray-50">
              {/* Search + New button */}
              <div className="p-3 border-b flex flex-col gap-2">
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search profiles…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-8 pr-3 py-1.5 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
                  />
                </div>
                <button
                  onClick={handleNew}
                  className="flex items-center justify-center gap-1.5 bg-green-600 hover:bg-green-700 text-white text-sm py-1.5 rounded transition"
                >
                  <Plus size={14} /> New Profile
                </button>
              </div>

              {/* List */}
              <div className="flex-1 overflow-y-auto">
                {filtered.length === 0 ? (
                  <p className="text-center text-gray-400 text-sm mt-8 px-4">
                    {search ? 'No matching profiles.' : 'No profiles yet. Click "New Profile" to add one.'}
                  </p>
                ) : (
                  filtered.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => handleSelect(p)}
                      className={`w-full text-left px-4 py-3 border-b hover:bg-green-50 transition ${
                        selectedId === p.id ? 'bg-green-100 border-l-4 border-l-green-600' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className={`text-sm font-medium ${p.active ? 'text-gray-900' : 'text-gray-400 line-through'}`}>
                          {p.name}
                        </span>
                        {!p.active && (
                          <span className="text-xs text-gray-400 ml-1">(inactive)</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        → {p.lab_crop_name} <span className="text-gray-400">({p.lab_crop_code})</span>
                      </div>
                      {p.notes && (
                        <div className="text-xs text-gray-400 mt-0.5 truncate">{p.notes}</div>
                      )}
                    </button>
                  ))
                )}
              </div>

              {/* Footer count */}
              <div className="px-4 py-2 border-t text-xs text-gray-400 text-right">
                {filtered.length} of {profiles.length} profile{profiles.length !== 1 ? 's' : ''}
              </div>
            </div>

            {/* ── RIGHT: editor ─────────────────────────────────────────── */}
            <div className="flex-1 flex flex-col overflow-y-auto p-6">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-base font-semibold text-gray-800">
                  {editId === null ? '✦ New Crop Profile' : `Edit: ${editName || '…'}`}
                </h3>
                <button
                  onClick={loadAll}
                  title="Refresh"
                  className="text-gray-400 hover:text-gray-600"
                >
                  <RefreshCw size={16} />
                </button>
              </div>

              {/* Feedback */}
              {error && (
                <div className="mb-4 px-3 py-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded">
                  {error}
                </div>
              )}
              {successMsg && (
                <div className="mb-4 px-3 py-2 bg-green-50 border border-green-200 text-green-700 text-sm rounded">
                  {successMsg}
                </div>
              )}

              <div className="grid grid-cols-1 gap-5 max-w-lg">

                {/* Profile Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Profile Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder="e.g. Wine Grapes, Table Grapes, Juice Grapes…"
                    className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    This is what users type in the "Crop" field during sample submission.
                  </p>
                </div>

                {/* PAL Lab Crop */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    PAL Lab Crop <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={editLabCropId}
                    onChange={(e) => setEditLabCropId(e.target.value)}
                    className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
                  >
                    <option value="">— select PAL crop —</option>
                    {activeLabCrops.map((lc) => (
                      <option key={lc.id} value={lc.id}>
                        {lc.pal_name} ({lc.code})
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-400 mt-1">
                    The official PAL crop code used in the lab submission CSV.
                  </p>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    placeholder="Optional notes (e.g. 'use for fresh-market table grape varieties')"
                    rows={3}
                    className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 resize-none"
                  />
                </div>

                {/* Active toggle */}
                <div className="flex items-center gap-3">
                  <input
                    id="cp-active"
                    type="checkbox"
                    checked={editActive}
                    onChange={(e) => setEditActive(e.target.checked)}
                    className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                  />
                  <label htmlFor="cp-active" className="text-sm text-gray-700">
                    Active (inactive profiles are hidden from submission forms)
                  </label>
                </div>
              </div>

              {/* ── Action buttons ─────────────────────────────────────── */}
              <div className="flex items-center gap-3 mt-8">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white px-5 py-2 rounded text-sm font-medium transition"
                >
                  <Save size={15} />
                  {saving ? 'Saving…' : editId === null ? 'Create Profile' : 'Save Changes'}
                </button>

                {editId !== null && (
                  <button
                    onClick={handleDelete}
                    className="flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-sm font-medium transition"
                  >
                    <Trash2 size={15} />
                    Delete
                  </button>
                )}

                <button
                  onClick={handleNew}
                  className="flex items-center gap-1.5 border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded text-sm font-medium transition"
                >
                  <Plus size={15} />
                  New
                </button>
              </div>

              {/* ── Help box ──────────────────────────────────────────── */}
              <div className="mt-10 p-4 bg-amber-50 border border-amber-200 rounded-lg max-w-lg">
                <h4 className="text-sm font-semibold text-amber-800 mb-1">How this works</h4>
                <p className="text-xs text-amber-700 leading-relaxed">
                  The PAL lab only recognises its own master list of crop codes (e.g. "Grapes" = 180,
                  "Corn" = 162). This manager lets you define additional crop names — like{' '}
                  <em>Wine Grapes</em>, <em>Table Grapes</em>, or <em>Raisins</em> — and link each
                  one to the closest matching PAL code. When a batch CSV is generated, the portal
                  automatically resolves the crop name to the correct code.
                </p>
                <p className="text-xs text-amber-700 mt-2 leading-relaxed">
                  Custom profile names also appear in the crop dropdown on the submission form so
                  users can select them directly.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CropProfilesModal;
